import { Box, Flex, BoxProps, Image } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import VideoComponent, { VideoComponentProps } from 'components/VideoComponent';
import { mergeHtmlVideoProps } from 'components/VideoComponent/utils';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import { ImageType } from 'utils/types';

import BenefitCardDescription, {
  BenefitCardDescriptionProps,
} from './BenefitCardDescription';

export type BenefitsCardsProps = {
  mobileImage?: ImageType | null;
  desktopImage?: ImageType | null;
  mobileVideo?: VideoComponentProps | null;
  desktopVideo?: VideoComponentProps | null;
  benefitsCardDescription?: BenefitCardDescriptionProps;
  slots?: BenefitsCardsSlotOverrides;
};

type SlotOverride = {
  sx?: any;
};

export type BenefitsCardsSlotOverrides = {
  root?: SlotOverride;
  descriptionContainer?: SlotOverride;
  mediaContainer?: SlotOverride;
  mobileMedia?: SlotOverride;
  desktopMedia?: SlotOverride;
  mobileImage?: SlotOverride;
  desktopImage?: SlotOverride;
  mobileVideoContainer?: SlotOverride;
  desktopVideoContainer?: SlotOverride;
  mobileImageContainer?: SlotOverride;
  desktopImageContainer?: SlotOverride;
};

const mergeSlotSx = (
  ...sxValues: Array<Record<string, any> | undefined>
) => {
  const merged = sxValues.reduce((acc, sxValue) => {
    if (!sxValue) {
      return acc;
    }

    return { ...acc, ...sxValue };
  }, {} as Record<string, any>);

  return merged && Object.keys(merged).length > 0 ? merged : undefined;
};

/**
 * BenefitsCards component
 * A component that displays a section with a benefit card description and a media element (video or image).
 * The media element changes based on screen size (mobile/desktop).
 * @param {ImageType | null} [props.mobileImage] Image to display on mobile screens
 * @param {ImageType | null} [props.desktopImage] Image to display on desktop screens
 * @param {VideoComponentProps | null} [props.mobileVideo] Video to display on mobile screens. If provided, it will ignore the mobileImage prop.
 * @param {VideoComponentProps | null} [props.desktopVideo] Video to display on desktop screens. If provided, it will ignore the desktopImage prop.
 * @param {BenefitCardDescriptionProps} [props.benefitsCardDescription] Props for the BenefitCardDescription component
 * @returns {JSX.Element} BenefitsCards component
 */
export default function BenefitsCards({
  mobileImage,
  desktopImage,
  mobileVideo,
  desktopVideo,
  benefitsCardDescription,
  slots,
}: BenefitsCardsProps) {
  // State to track if page is loaded to play video
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);

  // Effect to set shouldPlayVideo to true after component mounts
  useEffect(() => {
    setShouldPlayVideo(true);
  }, []);

  const displayMobileConfig: BoxProps['display'] = ['flex', null, null, 'none'];
  const displayDesktopConfig: BoxProps['display'] = [
    'none',
    null,
    null,
    'flex',
  ];

  const mobilePosterSrc =
    mobileImage?.gatsbyImageData?.images?.fallback?.src || null;
  const desktopPosterSrc =
    desktopImage?.gatsbyImageData?.images?.fallback?.src || null;

  const sharedVideoStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'left',
  } as const;

  const mobileVideoHtmlProps = mobileVideo
    ? mergeHtmlVideoProps(mobileVideo, {
        loop: true,
        autoPlay: shouldPlayVideo,
        muted: true,
        playsInline: true,
        poster: mobilePosterSrc ?? undefined,
        style: sharedVideoStyle,
      })
    : undefined;

  const desktopVideoHtmlProps = desktopVideo
    ? mergeHtmlVideoProps(desktopVideo, {
        loop: true,
        autoPlay: shouldPlayVideo,
        muted: true,
        playsInline: true,
        poster: desktopPosterSrc ?? undefined,
        style: sharedVideoStyle,
      })
    : undefined;

  return (
    <SectionContainer
      size={['sm', null, null, 'xl']}
      paddingX={[4, null, null, 12]}
      paddingY={[4, null, null, 0]}
      display="flex"
      flexDirection={['column', null, null, 'row-reverse']}
      gap={[2, null, null, 4]}
      data-component="BenefitsCards"
      sx={mergeSlotSx(slots?.root?.sx)}
    >
      <Flex
        width={['100%', null, null, '440px']}
        minHeight={['auto', null, null, '640px']}
        sx={mergeSlotSx(slots?.descriptionContainer?.sx)}
      >
        <BenefitCardDescription {...benefitsCardDescription} />
      </Flex>
      {/* Video or Image Card */}
      <Flex
        flex="1"
        borderRadius="3xl"
        overflow="hidden"
        sx={mergeSlotSx(slots?.mediaContainer?.sx)}
      >
        {/* Mobile Video or Image - Check for video first   */}
        {mobileVideo ? (
          <Box
            display={displayMobileConfig}
            height={['360px', 'auto']}
            width="100%"
            sx={
              mergeSlotSx(
                slots?.mobileMedia?.sx,
                slots?.mobileVideoContainer?.sx,
              )
            }
          >
            <VideoComponent
              {...mobileVideo}
              htmlVideoProps={mobileVideoHtmlProps}
            />
          </Box>
        ) : mobileImage ? (
          <Box
            display={displayMobileConfig}
            sx={
              mergeSlotSx(
                slots?.mobileMedia?.sx,
                slots?.mobileImageContainer?.sx,
              )
            }
          >
            <Image
              image={mobileImage.gatsbyImageData}
              as={GatsbyImage}
              sx={mergeSlotSx(slots?.mobileImage?.sx)}
            />
          </Box>
        ) : null}

        {/* Desktop Video or Image - Check for video first */}
        {desktopVideo ? (
          <Box
            display={displayDesktopConfig}
            width="100%"
            sx={
              mergeSlotSx(
                slots?.desktopMedia?.sx,
                slots?.desktopVideoContainer?.sx,
              )
            }
          >
            <VideoComponent
              {...desktopVideo}
              htmlVideoProps={desktopVideoHtmlProps}
            />
          </Box>
        ) : desktopImage ? (
          <Box
            display={displayDesktopConfig}
            sx={
              mergeSlotSx(
                slots?.desktopMedia?.sx,
                slots?.desktopImageContainer?.sx,
              )
            }
          >
            <Image
              image={desktopImage.gatsbyImageData}
              as={GatsbyImage}
              sx={mergeSlotSx(slots?.desktopImage?.sx)}
            />
          </Box>
        ) : null}
      </Flex>
    </SectionContainer>
  );
}
